import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { createDummyEvent } from '@/dummyData';
import { useBackend } from '@/hooks/useBackend';
import { useEventFilterParams } from '@/hooks/useEventFilterParams';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import { PaginatedResults } from '@/pages/explorer/components/PaginatedResults';
import EventList from '@/pages/explorer/events/EventList';

const DUMMY_EVENTS = new Array(5).fill(null).map((_, id) => createDummyEvent(id));

export function PaginatedEvents() {
  const [searchParams] = useSearchParams();
  const filterParams = useEventFilterParams();
  const page = Math.max(Number(searchParams.get('page')) || 0, 0);

  const swrExpect404 = useSWRExpect404();

  const {
    data: eventResponse,
    isLoading,
    error,
    mutate,
  } = useBackend(
    '/events',
    { params: { query: { page, ...filterParams } } },
    { keepPreviousData: true, ...swrExpect404 },
  );

  const onFavoriteChange = useCallback(
    (eventId: string, isFavorite: boolean) => {
      void mutate(
        (prev) => {
          if (!prev) return;
          const updated = structuredClone(prev);
          const event = updated.content.find((e) => e.id === eventId);
          if (event) {
            event.isFavorite = isFavorite;
          }
          return updated;
        },
        { revalidate: false },
      );
    },
    [mutate],
  );

  return (
    <PaginatedResults
      data={eventResponse}
      emptyMessage="No events match the selected filters."
      error={error}
      errorMessage="Failed to fetch events."
      isLoading={isLoading}
      itemLabel={{ singular: 'event', plural: 'events' }}
      renderItems={(events) => <EventList events={events} onFavoriteChange={onFavoriteChange} />}
      renderSkeleton={() => <EventList events={DUMMY_EVENTS} onFavoriteChange={() => {}} />}
    />
  );
}
